import React, { useState, useContext, useEffect } from 'react';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import './CritControl.css';

// Crit mode options matching backend enum
const CRIT_MODES = [
  { value: 'AlwaysCrit', label: 'Always Crit', description: 'All attacks that can crit will always crit' },
  { value: 'NeverCrit', label: 'Never Crit', description: 'Attacks will never critically strike' },
  { value: 'RandomCrit', label: 'Random Crit', description: 'Crit chance determines probability of critical strike. Results vary each time and are not cached.' },
  { value: 'ExpectedValue', label: 'Expected Value', description: 'Damage is calculated as average expected damage based on crit chance' }
];

interface CritControlProps {
  championId: string;
  currentCritMode: string;
  onCritModeUpdate?: (critMode: string) => void;
}

const CritControl: React.FC<CritControlProps> = ({ championId, currentCritMode, onCritModeUpdate }) => {
  const socket = useContext(SocketContext);
  const [selectedMode, setSelectedMode] = useState(currentCritMode || 'ExpectedValue');
  const [showTooltip, setShowTooltip] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    if (currentCritMode) {
      setSelectedMode(currentCritMode);
    }
  }, [currentCritMode]);

  const handleModeChange = (newMode: string) => {
    setSelectedMode(newMode);
    
    // Use callback if provided (static mode), otherwise use socket (WebSocket mode)
    if (onCritModeUpdate) {
      onCritModeUpdate(newMode);
    } else if (socket) {
      socket.emit('set-crit-mode', { championId, critMode: newMode });
    }
  };

  const selectedModeInfo = CRIT_MODES.find(m => m.value === selectedMode);

  return (
    <div className="control-container crit-control">
      <div className="control-header">
        <span className="control-label">Crit Mode</span>
        <span 
          className="crit-info-icon"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          ⓘ
        </span>
        {showTooltip && (
          <div className="crit-info-tooltip">
            By default, <strong>Expected Value</strong> crit is used as it provides the most consistent 
            and comparable results between champions and builds. You can change to other modes if needed. 
            Note: <strong>Random Crit</strong> results are not stored since they vary each simulation.
          </div>
        )}
      </div>
      <div className="crit-mode-selector">
        {CRIT_MODES.map((mode) => (
          <button
            key={mode.value}
            className={`crit-mode-button ${selectedMode === mode.value ? 'active' : ''}`}
            onClick={() => handleModeChange(mode.value)}
            title={mode.description}
          >
            {mode.label}
          </button>
        ))}
      </div>
      {selectedModeInfo && (
        <div className="crit-mode-description">
          {selectedModeInfo.description}
        </div>
      )}
    </div>
  );
};

export default CritControl;
